"use client";

import { tsv } from "@faire/slate/lib/tsv";
import { useFloatingContext } from "@faire/slate/private/floating/FloatingContext";
import { FloatingList, useMergeRefs } from "@floating-ui/react";
import React from "react";

import { useMenuContext } from "./MenuContext";

type IProps = React.HtmlHTMLAttributes<HTMLUListElement> & {
  ref?: React.Ref<HTMLUListElement>;
  zIndex?: number;
  hasIcons?: boolean;
};

export const MenuBase: React.FC<IProps> = ({
  children,
  style,
  className,
  ref,
  zIndex,
  hasIcons,
  ...props
}) => {
  const { refs, floatingStyles, propGetters } = useFloatingContext();
  const { itemsRef } = useMenuContext();

  const mergedRef = useMergeRefs([refs.setFloating, ref]);

  const listStyleVars = listStyle.vars({ zIndex });
  const floatingProps = propGetters.getFloatingProps({
    ...props,
    ref: mergedRef,
    style: { ...style, ...listStyleVars, ...floatingStyles },
    className: listStyle.cls({ hasIcons, className }),
  });

  if (props["aria-label"] && !props["aria-labelledby"]) {
    // By default, Floating UI will set aria-labelledby using the trigger element.
    // However, aria-labelledby takes precedence over aria-label and this is not intuitive for users.
    floatingProps["aria-labelledby"] = undefined;
  }
  return (
    <ul {...floatingProps}>
      <FloatingList elementsRef={itemsRef}>{children}</FloatingList>
    </ul>
  );
};

const listStyle = tsv({
  vars: {
    zIndex: "fs-menu-z-index",
  },
  base: "fs-menu z-(--fs-menu-z-index)",
  variants: {
    hasIcons: {
      true: "fs-menu--has-icons",
    },
  },
});
